import {useState} from 'react';
import {Controller} from 'react-hook-form';
import {Link, Navigate, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '../../ui/forms/input-field/text-field/text-field';
import {Form} from '../../ui/forms/form';
import {RegisterPayload, useRegister} from '../requests/use-register';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '../../i18n/trans';
import {useRecaptcha} from '../../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useSettings} from '../../core/settings/use-settings';
import {
  AuthBackButton,
  AuthBirthDateField,
  AuthCard,
  AuthCardWrap,
  AuthCheckbox,
  AuthCloseButton,
  AuthCountrySelect,
  AuthEmailDisplay,
  AuthPromptCard,
  AuthShell,
  AuthTitle,
  authFieldClass,
  authFieldHalfClass,
  authPrimaryBtnClass,
} from './auth-ui';
import {resolveSiteLogoSrc} from '@common/branding/site-logo';
import {useValueLists} from '@common/http/value-lists';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import clsx from 'clsx';

type RegisterFormValues = RegisterPayload & {
  privacyPolicy: boolean;
  first_name?: string;
  last_name?: string;
  birth_date?: string;
  country?: string;
};

export function RegisterPage() {
  const {
    branding,
    registration: {disable},
  } = useSettings();
  const {verify, isVerifying} = useRecaptcha('register');
  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const isDarkMode = useIsDarkMode();

  const isWorkspaceRegister = pathname.includes('workspace');
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const searchParamsEmail = searchParams.get('email') || undefined;

  const form = useForm<RegisterFormValues>({
    defaultValues: {
      email: searchParamsEmail,
      privacyPolicy: false,
    },
  });
  const register = useRegister<RegisterFormValues>(form);
  useValueLists(['countries']);

  if (disable) {
    return <Navigate to="/login" replace />;
  }

  const logoSrc = resolveSiteLogoSrc(branding, isDarkMode);

  const goToStep2 = async () => {
    if (!(form.getValues('email') ?? '').trim()) return;
    const emailValid = await form.trigger('email');
    if (!emailValid) return;
    if (!form.getValues('privacyPolicy')) {
      form.setError('privacyPolicy', {
        type: 'manual',
        message: 'You must agree to the terms',
      });
      return;
    }
    form.clearErrors('privacyPolicy');
    setCurrentStep(2);
  };

  return (
    <AuthLayout>
      <StaticPageTitle>
        <Trans message="Register" />
      </StaticPageTitle>
      <AuthShell logoSrc={logoSrc}>
        <AuthCardWrap
          compact
          action={
            currentStep === 1 ? (
              <AuthCloseButton />
            ) : (
              <AuthBackButton onClick={() => setCurrentStep(1)} />
            )
          }
        >
          <AuthCard compact>
            <Form
              form={form}
              onSubmit={async (values: RegisterFormValues) => {
                const fieldsValid = await form.trigger([
                  'password',
                  'country',
                  'first_name',
                  'last_name',
                ]);
                if (!fieldsValid) return;

                const isValid = await verify();
                if (!isValid) return;
                const {
                  privacyPolicy,
                  first_name,
                  last_name,
                  birth_date,
                  country,
                  ...payload
                } = values;
                register.mutate({
                  ...payload,
                  password_confirmation: payload.password,
                } as RegisterPayload);
              }}
            >
              <AuthTitle>
              {isWorkspaceRegister ? (
                <Trans
                  values={{siteName: branding?.site_name}}
                  message="To join your team on :siteName, create an account"
                />
              ) : isBillingRegister ? (
                <Trans message="First, let's create your account" />
              ) : (
                <Trans message="Create your account" />
              )}
            </AuthTitle>

            {currentStep === 1 ? (
              <div className="flex w-full flex-col items-center gap-[24px]">
                <FormTextField
                  name="email"
                  type="email"
                  label={null}
                  placeholder="Email"
                  className="w-[350px] max-w-full"
                  inputElementType="input"
                  inputClassName={authFieldClass}
                  required
                  onChange={() => form.clearErrors('email')}
                />

                <Controller
                  name="privacyPolicy"
                  control={form.control}
                  rules={{required: true}}
                  render={({field, fieldState}) => (
                    <AuthCheckbox
                      id="privacyPolicy"
                      align="center"
                      checked={!!field.value}
                      onChange={field.onChange}
                      hasError={!!fieldState.error}
                    >
                      I agree to the{' '}
                      <Link
                        to="/pages/terms-of-service"
                        className="font-medium text-[#3C7D44] no-underline hover:text-[#2B7E20] dark:text-[#64B466] dark:hover:text-[#3C7D44]"
                        target="_blank"
                      >
                        Terms
                      </Link>{' '}
                      &{' '}
                      <Link
                        to="/pages/privacy-policy"
                        className="font-medium text-[#3C7D44] no-underline hover:text-[#2B7E20] dark:text-[#64B466] dark:hover:text-[#3C7D44]"
                        target="_blank"
                      >
                        Privacy policy
                      </Link>
                    </AuthCheckbox>
                  )}
                />

                <button
                  type="button"
                  onClick={goToStep2}
                  className={authPrimaryBtnClass}
                  aria-label="Create account"
                >
                  <Trans message="Create" />
                </button>
              </div>
            ) : (
              <div className="flex w-full flex-col items-center gap-[16px]">
                <AuthEmailDisplay email={form.watch('email') || ''} />

                <FormTextField
                  name="password"
                  type="password"
                  label={null}
                  placeholder="Password"
                  className="w-[350px] max-w-full"
                  inputClassName={authFieldClass}
                  required
                />

                <div className="flex w-[350px] max-w-full gap-[20px]">
                  <FormTextField
                    name="first_name"
                    type="text"
                    label={null}
                    placeholder="First Name"
                    className="w-[163px] shrink-0"
                    inputClassName={authFieldHalfClass}
                  />
                  <FormTextField
                    name="last_name"
                    type="text"
                    label={null}
                    placeholder="Last Name"
                    className="w-[163px] shrink-0"
                    inputClassName={authFieldHalfClass}
                  />
                </div>

                <div className="flex w-[350px] max-w-full gap-[20px]">
                  <AuthBirthDateField name="birth_date" />
                  <AuthCountrySelect name="country" />
                </div>

                <button
                  type="submit"
                  disabled={register.isLoading || isVerifying}
                  className={clsx(authPrimaryBtnClass, 'mt-[8px]')}
                  aria-label="Create account"
                >
                  {register.isLoading ? (
                    <Trans message="Creating..." />
                  ) : (
                    <Trans message="Create" />
                  )}
                </button>
              </div>
            )}
            </Form>
          </AuthCard>
        </AuthCardWrap>

        {currentStep === 1 && (
          <AuthPromptCard
            text={<Trans message="Have a CloudTransfer account?" />}
            buttonLabel="Login"
            buttonTo="/login"
          />
        )}
      </AuthShell>
    </AuthLayout>
  );
}
